import { MapViewModel } from '../model/MapViewModel.ts';
import { ILocation } from '../../support/model/ILocation.ts';
import { ISuggestion } from '../../support/model/ISuggestion.ts';
import { ICoordinate } from '../../support/model/ICoordinate.ts';
import { EnvConfiguration } from '../../app/config/EnvConfiguration.ts';
import { ReverseGeocodingService, cleanMapboxText, fromGeocoding } from './ReverseGeocodingService.ts';

const INCLUDED_CATEGORIES = [
    'airport',
    'beach',
    'bridge',
    'campground',
    'canal',
    'church',
    'dog_park',
    'education',
    'factory',
    'field',
    'forest',
    'golf_course',
    'historic_site',
    'island',
    'lake',
    'lighthouse',
    'marina',
    'monument',
    'mountain',
    'museum',
    'nature_reserve',
    'park',
    'pier',
    'playground',
    'racetrack',
    'railway_station',
    'river',
    'ski_area',
    'sports_center',
    'stadium',
    'swimming_pool',
    'tennis_courts',
    'theater',
    'theme_park',
    'tourist_attraction',
    'tunnel',
    'viewpoint',
    'vineyard',
    'warehouse',
    'waterfall',
    'windmill',
    'winery',
    'zoo',
];

// Adapter for Search Box v1 Suggest API.
// This API returns structured fields, so we can keep the name separate from the address text.
const fromSuggest = ({ feature_type, name, full_address, place_formatted }: { feature_type: string, name: string, full_address: string, place_formatted: string }) => {
    if (feature_type === 'poi') {
        return { name, addressText: cleanMapboxText(full_address) };
    }
    if (feature_type === 'neighborhood') {
        return { name, addressText: cleanMapboxText(place_formatted) };
    }
    if (feature_type === 'address') {
        return { addressText: cleanMapboxText(full_address) };
    }
    // street, place/city: the name is part of the address (e.g. street name, city name)
    return { addressText: `${name}, ${cleanMapboxText(place_formatted)}` };
};

export class LocationSearchService {
    static inject = () => [MapViewModel, EnvConfiguration, ReverseGeocodingService];
    constructor(private map: MapViewModel, private config: EnvConfiguration, private reverseGeocoding: ReverseGeocodingService){}

    private getProximity = () => {
        if (this.map.isInitialized() && !this.map.isInitialCenter()){
            // Bias the search results to the wherever the user last left the center of the map
            const mapCenter = this.map.getCenter();
            return `${mapCenter.lng},${mapCenter.lat}`;
        } else {
            // If this is the first search by a new user who has denied location permission, we don't have a useful map center.
            // So for this edge case, bias towards whatever location mapbox can infer from the ip address. This is likely inaccurate
            // and we have FUD about whether it's working as expected (because we have seen different results in staging v prod).
            // So note that in app tests, we avoid hitting this path by panning the map a bit before each test.
            return 'ip';
        }
    };

    search = async ({ searchText, matchesToReturn }: { searchText: string, matchesToReturn: number }): Promise<ILocation[]> => {
        if (!searchText) {
            return [];
        }

        const params = new URLSearchParams();
        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('country', 'us');
        params.set('limit', String(matchesToReturn)); // max 10
        params.set('types', 'place,poi,address');
        params.set('worldview', 'us');
        params.set('proximity', this.getProximity());

        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchText}.json?${params.toString()}`;

        const result = await fetch(url);
        const { features } = await result.json();

        if (!features?.length) {
            console.log('No features returned by mapbox', {searchText});
            return [];
        }

        return features.map(({ center, place_name, place_type }: { center: number[], place_name: string, place_type: string[] }) => ({
            address: this.reverseGeocoding.parseAddress(fromGeocoding({ place_name, place_type })),
            coordinates: { lng: center[0], lat: center[1] }
        }));
    };

    suggest = async ({ searchText, matchesToReturn, sessionToken }: { searchText: string, matchesToReturn: number, sessionToken: string }): Promise<ISuggestion[]> => {
        if (!searchText) {
            return [];
        }

        const params = new URLSearchParams();

        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('session_token', sessionToken);
        params.set('q', searchText);
        params.set('country', 'us');
        params.set('limit', String(matchesToReturn)); // max 10
        params.set('types', 'address,street,city,neighborhood,poi');
        params.set('poi_category', INCLUDED_CATEGORIES.join(','));
        params.set('proximity', this.getProximity());

        const url = `https://api.mapbox.com/search/searchbox/v1/suggest?${params.toString()}`;

        const result = await fetch(url);
        const { suggestions } = await result.json();

        if (!suggestions?.length) {
            console.log('No suggestions returned by mapbox', {searchText});
            return [];
        }

        return suggestions.filter(Boolean).map(({ mapbox_id, feature_type, name, place_formatted, full_address }: { mapbox_id: string, feature_type: string, name: string, place_formatted: string, full_address: string }) => ({
            address: this.reverseGeocoding.parseAddress(fromSuggest({ feature_type, name, full_address, place_formatted })),
            id: mapbox_id
        }));
    };

    retrieve = async ({ id, sessionToken }: { id: string, sessionToken: string }): Promise<ICoordinate> => {
        const params = new URLSearchParams();
        params.set('access_token', this.config.MAPBOX_TOKEN);
        params.set('session_token', sessionToken);

        const url = `https://api.mapbox.com/search/searchbox/v1/retrieve/${id}?${params.toString()}`;

        const result = await fetch(url);
        const { features } = await result.json();

        if (features?.length !== 1) {
            throw new Error(`Expected /retrieve to return 1 feature, but got ${features?.length || 0}`);
        }

        const center = features[0].geometry.coordinates;
        return {
            lng: center[0],
            lat: center[1]
        };
    };
}
